import React, { useState, useEffect, useMemo } from 'react';
import { 
  Clock, 
  RefreshCw,
  List,
  ChevronDown, 
  ChevronUp,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useTimeEntries } from '../hooks/useTimeEntries';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { supabase } from '../lib/supabase';
import EnhancedQuickStats from './EnhancedQuickStats';
import TeamViewToggle from './TeamViewToggle';
import SplitTimeModal from './SplitTimeModal';
import TimeEntryCard from './time-entries/TimeEntryCard';
import CommentsPanel from './time-entries/CommentsPanel';
import StatusBadge from './time-entries/StatusBadge';
import TagsManager from './time-entries/TagsManager';
import WorkflowActions from './time-entries/WorkflowActions';
import CalendarViewModeSelector from './time-entries/CalendarViewModeSelector';
import TimeEntryCell from './time-entries/TimeEntryCell';
import CalendarView from './time-entries/CalendarView';
import EnhancedFilters from './time-entries/EnhancedFilters';
import BulkActions from './time-entries/BulkActions';
import GroupBySelector from './time-entries/GroupBySelector';
import GroupedEntriesView from './time-entries/GroupedEntriesView';

const MainContent = React.memo(({
  viewMode,
  timeEntries,
  groupBy,
  clients,
  projects,
  teamMembers,
  selectedEntries,
  onToggleSelection,
  onEdit,
  onDuplicate,
  onDelete,
  onView,
  onStatusUpdate,
  onSplit,
  onRevertSplit,
  onAddComment,
  onUpdateTags,
  userRole,
  currentDate,
  onDateChange,
  onEntryCreate,
  goals
}) => {
  if (viewMode === 'list') {
    return (
      <div className="space-y-4">
        {timeEntries.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
            <Clock className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-800 mb-2">No entries found</h3>
            <p className="text-slate-600">Try adjusting your filters or start tracking some time!</p>
          </div>
        ) : groupBy !== 'none' ? (
          // Grouped View
          <GroupedEntriesView
            key={`${groupBy}-${timeEntries.length}`} // Key to force re-mount only on groupBy/data change
            entries={timeEntries}
            groupBy={groupBy}
            clients={clients}
            projects={projects}
            teamMembers={teamMembers}
            selectedEntries={selectedEntries}
            onToggleSelection={onToggleSelection}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
            onView={onView}
            onStatusUpdate={onStatusUpdate}
            onSplit={onSplit}
            onRevertSplit={onRevertSplit}
            onAddComment={onAddComment}
            onUpdateTags={onUpdateTags}
            userRole={userRole}
            teamMembers={teamMembers}
          />
        ) : (
        // Standard List View
        timeEntries.map(entry => (
          <div key={entry.id} className="relative">
            {/* Selection Checkbox */}
            <div className="absolute left-2 top-4 z-10">
              <input
                type="checkbox"
                checked={selectedEntries.includes(entry.id)}
                onChange={() => onToggleSelection(entry.id)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
            </div>
            
            <div className="ml-8">
                <TimeEntryCard
                  key={entry.id} // Ensure stable key
                  entry={{
                    ...entry,
                    comments: entry.comments || [],
                    tags: entry.tags || [],
                    priority: entry.priority || 'medium'
                  }}
                  clients={clients}
                  projects={projects}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                  onView={onView}
                  onStatusUpdate={onStatusUpdate}
                  onSplit={onSplit}
                  onRevertSplit={onRevertSplit}
                  onAddComment={onAddComment}
                  onUpdateTags={onUpdateTags}
                  userRole={userRole}
                  teamMembers={teamMembers}
                />
            </div>
          </div>
        ))
      )}
      </div>
    );
  }

  return (
    <CalendarView
      key={`${viewMode}-${currentDate.toDateString()}`} // Key to re-mount only on view/date change
      timeEntries={timeEntries}
      clients={clients}
      projects={projects}
      currentDate={currentDate}
      viewMode={viewMode}
      groupBy={groupBy} // Pass groupBy for calendar grouping
      onDateChange={onDateChange}
      onEntryEdit={onEdit}
      onEntryDuplicate={onDuplicate}
      onEntryView={onView}
      onEntryCreate={onEntryCreate}
      onStatusUpdate={onStatusUpdate}
      onAddComment={onAddComment}
      onUpdateTags={onUpdateTags}
      userRole={userRole}
      teamMembers={teamMembers}
      goals={goals}
    />
  );
});

const EnhancedEntriesView = () => {
  const { user } = useAuth();
  const { currentTenant, userRole } = useTenant();
  
  // View state
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [currentDate, setCurrentDate] = useState(new Date());
  const [teamViewMode, setTeamViewMode] = useState('team'); // 'team' or 'my'
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  
  // Handle view action - switch to list view with single entry
  const handleEntryView = (entry) => {
    // Switch to list view
    setViewMode('list');
    
    // Set filters to show only this entry
    setFilters(prevFilters => ({
      ...prevFilters,
      search: entry.task_name, // or use entry ID filter if available
      entryId: entry.id // If you have an entry ID filter
    }));
    
    // Store the selected entry ID for highlighting
    setSelectedEntryId(entry.id);
    // Scroll to and highlight the entry after a brief delay
    setTimeout(() => {
      const entryElement = document.querySelector(`[data-entry-id="${entry.id}"]`);
      if (entryElement) {
        entryElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Add temporary highlight
        entryElement.classList.add('highlight-entry');
        setTimeout(() => {
          entryElement.classList.remove('highlight-entry');
        }, 2000);
      }
    }, 100);
  };
  
  // Filters and selection
  const [filters, setFilters] = useState({
    search: '',
    clientId: 'all',
    projectId: 'all',
    status: 'all', // Default to exclude approved entries
    priority: 'all',
    billable: 'all',
    startDate: '',
    endDate: '',
    tags: '',
    userId: 'all',  // Will be set based on teamViewMode
	 draftAge: 'all' 
  });

  const [selectedEntries, setSelectedEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New state for split functionality
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [selectedEntryForSplit, setSelectedEntryForSplit] = useState(null);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('entry_date'); // Default sort by submitted_at
  const [sortOrder, setSortOrder] = useState('desc'); // Newest submitted first

  // Goals
  const [goals] = useState({ dailyHours: 8, weeklyHours: 40 });

  // Use the useTimeEntries hook
  const { timeEntries, loading: entriesLoading, createTimeEntry, updateTimeEntry, deleteTimeEntry, refetch } = 
    useTimeEntries({ 
     // ...filters, 
      userId: (userRole === 'admin' || userRole === 'manager') && teamViewMode === 'my' ? user.id : filters.userId,
      sortBy, 
      sortOrder 
    });
  
  // Sort change handler
  const handleSortChange = (newSortBy, newSortOrder) => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
  };
  
  // Add this handler function
  const handleTeamViewChange = (newViewMode) => {
    setTeamViewMode(newViewMode);
    
    // If switching to 'my' view, add user filter
    // If switching to 'team' view, remove user filter
    const newUserId = newViewMode === 'my' ? user.id : 'all';
    
    setFilters(prev => ({
      ...prev,
      userId: newUserId
    }));
  };

  const [groupBy, setGroupBy] = useState('none'); // 'none', 'user', 'role', 'client', 'project'

  // Helper functions
  const formatTime = (hours) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };
  const formatCurrency = (amount) => `$${(amount || 0).toFixed(2)}`;
  const isManager = () => userRole === 'admin' || userRole === 'manager';

  // Load clients, projects, and team members
  useEffect(() => {
    const loadData = async () => {
      if (!currentTenant) return;

      try {
        setLoading(true);
        
        // Load clients
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select('*')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (clientsError) throw clientsError;

        // Load projects
        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .eq('tenant_id', currentTenant.id)
          .order('name');

        if (projectsError) throw projectsError;

        // Load team members
        const { data: teamData, error: teamError } = await supabase
          .from('tenant_memberships')
          .select(`
            user_id,
            role,
            user_profiles!tenant_memberships_user_id_fkey (
              full_name,
              email
            )
          `)
          .eq('tenant_id', currentTenant.id)
          .eq('status', 'active');

        if (teamError) {
          console.warn('Error loading team members:', teamError);
          setTeamMembers([]);
        } else {
          setTeamMembers(teamData?.map(member => ({
            user_id: member.user_id,
            role: member.role,
            full_name: member.user_profiles?.full_name,
            email: member.user_profiles?.email
          })) || []);
        }

        setClients(clientsData || []);
        setProjects(projectsData || []);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentTenant]);

  // Handle split time entry
  const handleSplitEntry = (entry) => {
    setSelectedEntryForSplit(entry);
    setShowSplitModal(true);
  };

  // Handle split complete
  const handleSplitComplete = async (entryId, splitData) => {
    console.log('Split completed:', entryId, splitData);
    setShowSplitModal(false);
    setSelectedEntryForSplit(null);
    // Refetch data to show updated entries
    await refetch();
  };

  // Handle revert split
  const handleRevertSplit = async (entry) => {
    if (!window.confirm('Are you sure you want to revert this split and return to the original single entry?')) {
      return;
    }

    try {
      const { data, error } = await supabase.rpc('revert_time_entry_split', {
        p_entry_id: entry.id
      });

      if (error) throw error;

      console.log('Split reverted successfully');
      await refetch(); // Refresh the entries list
    } catch (err) {
      console.error('Error reverting split:', err);
      alert('Failed to revert split: ' + err.message);
    }
  };

  // Event handlers
  const handleStatusUpdate = async (entryId, newStatus, comment, assignedTo) => {
    const updates = { status: newStatus };
    
    if (newStatus === 'submitted') {
      updates.submitted_at = new Date().toISOString();
    } else if (newStatus === 'approved') {
      updates.approved_at = new Date().toISOString();
      updates.approved_by = user.id;
    } else if (newStatus === 'rejected') {
      updates.rejected_at = new Date().toISOString();
      updates.rejected_by = user.id;
      if (comment) {
        updates.rejection_reason = comment;
      }
    }

    if (assignedTo) {
      updates.assigned_to = assignedTo;
    }

    const { error } = await updateTimeEntry(entryId, updates);
    
    if (error) {
      alert('Error updating entry: ' + error.message);
    } else {
      // Add comment if provided
      if (comment) {
        await handleAddComment(entryId, comment);
      }
    }
  };

  const handleEditEntry = async (entryId, updates) => {
    try {
      // Handle both entry object and entryId being passed
      let actualEntryId = entryId;
      let actualUpdates = updates;
      
      // If first parameter is an entry object (from calendar), extract the ID
      if (typeof entryId === 'object' && entryId.id) {
        actualEntryId = entryId.id;
        actualUpdates = updates || {}; // updates might be undefined from calendar
      }
      
      // Find the original entry
      const originalEntry = timeEntries.find(entry => entry.id === actualEntryId);
      
      if (!originalEntry) {
        console.error('Original entry not found for ID:', actualEntryId);
        alert('Error: Could not find the original entry to update.');
        return;
      }
      
      // Check if user is being reassigned (admin only)
      const isReassigning = actualUpdates.user_id && actualUpdates.user_id !== originalEntry.user_id;
      
      if (isReassigning && userRole === 'admin') {
        // Add audit log for user reassignment
        actualUpdates.reassigned_at = new Date().toISOString();
        actualUpdates.reassigned_by = user.id;
        actualUpdates.previous_user_id = originalEntry.user_id;
        
        // Add a system comment about the reassignment
        const reassignedUser = teamMembers.find(member => member.user_id === actualUpdates.user_id);
        const previousUser = teamMembers.find(member => member.user_id === originalEntry.user_id);
        
        const reassignmentComment = `Time entry reassigned from ${previousUser?.full_name || previousUser?.email || 'Unknown User'} to ${reassignedUser?.full_name || reassignedUser?.email || 'Unknown User'} by admin.`;
        
        // First update the time entry
        const { error: updateError } = await updateTimeEntry(actualEntryId, actualUpdates);
        
        if (updateError) {
          alert('Error updating entry: ' + updateError.message);
          return;
        }
        
        // Then add the reassignment comment
        try {
          await supabase
            .from('time_entry_comments')
            .insert({
              time_entry_id: actualEntryId,
              user_id: user.id,
              comment_text: reassignmentComment,
              is_system_comment: true
            });
        } catch (commentError) {
          console.warn('Failed to add reassignment comment:', commentError);
        }
        
        alert('Time entry successfully reassigned!');
      } else {
        // Regular update without reassignment
        const { error } = await updateTimeEntry(actualEntryId, actualUpdates);
        
        if (error) {
          alert('Error updating entry: ' + error.message);
        }
      }
    } catch (error) {
      console.error('Error in handleEditEntry:', error);
      alert('Error updating entry: ' + error.message);
    }
  };

  const handleDuplicateEntry = async (entry) => {
    try {
      const duplicatedEntry = {
        client_id: entry.client_id,
        project_id: entry.project_id,
        task_name: entry.task_name,
        description: entry.description,
        duration_hours: entry.duration_hours,
        is_billable: entry.is_billable,
        entry_date: new Date().toISOString().split('T')[0], // Today's date
        status: 'draft'
      };

      const { data, error } = await createTimeEntry(duplicatedEntry);
      
      if (error) {
        alert('Error duplicating entry: ' + error.message);
        return;
      }
      
      alert('Entry duplicated successfully!');
    } catch (error) {
      console.error('Error duplicating entry:', error);
      alert('Error duplicating entry: ' + error.message);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      const { error } = await deleteTimeEntry(entryId);
      
      if (error) {
        alert('Error deleting entry: ' + error.message);
      }
    }
  };

  const handleAddComment = async (entryId, comment) => {
    try {
      const { data: newComment, error } = await supabase
        .from('time_entry_comments')
        .insert({
          time_entry_id: entryId,
          user_id: user.id,
          comment_text: comment
        })
        .select(`
          id,
          comment_text,
          created_at,
          user_profiles!time_entry_comments_user_id_fkey(full_name, email)
        `)
        .single();

      if (error) throw error;
      
      refetch();
      
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment: ' + error.message);
    }
  };

  const handleUpdateTags = async (entryId, tags) => {
    try {
      const { error } = await updateTimeEntry(entryId, { tags });
      
      if (error) throw error;
    } catch (error) {
      console.error('Error updating tags:', error);
      alert('Error updating tags: ' + error.message);
    }
  };

  const handleBulkAction = async (action, entries) => {
    if (action === 'clear') {
      setSelectedEntries([]);
    } else {
      if (window.confirm(`Are you sure you want to ${action} ${entries.length} entries?`)) {
        try {
          const updates = {};
          
          switch (action) {
            case 'approve':
              updates.status = 'approved';
              updates.approved_at = new Date().toISOString();
              updates.approved_by = user.id;
              break;
            case 'reject':
              updates.status = 'rejected';
              updates.rejected_at = new Date().toISOString();
              updates.rejected_by = user.id;
              break;
            case 'submit':
              updates.status = 'submitted';
              updates.submitted_at = new Date().toISOString();
              break;
            case 'archive':
              updates.archived = true;
              break;
            case 'delete':
              for (const entryId of entries) {
                await deleteTimeEntry(entryId);
              }
              setSelectedEntries([]);
              return;
          }

          for (const entryId of entries) {
            await updateTimeEntry(entryId, updates);
          }
          
          setSelectedEntries([]);
        } catch (error) {
          console.error('Error performing bulk action:', error);
          alert('Error performing bulk action: ' + error.message);
        }
      }
    }
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleEntryCreate = async (entryData) => {
    try {
      const { data, error } = await createTimeEntry(entryData);
      
      if (error) {
        alert('Error creating entry: ' + error.message);
        return;
      }
      
      alert('Entry created successfully!');
    } catch (error) {
      console.error('Error creating entry:', error);
      alert('Error creating entry: ' + error.message);
    }
  };

  // Get visible entries based on current view mode
const visibleEntries = useMemo(() => {
  let entries = [];
  
  // First filter by view mode (day/week/month)
  if (viewMode === 'list') {
    entries = timeEntries;
  } else if (viewMode === 'day') {
    const dateStr = currentDate.toISOString().split('T')[0];
    entries = timeEntries.filter(entry => entry.entry_date === dateStr);
  } else if (viewMode === 'week') {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    const startStr = startOfWeek.toISOString().split('T')[0];
    const endStr = endOfWeek.toISOString().split('T')[0];
    
    entries = timeEntries.filter(entry => 
      entry.entry_date >= startStr && entry.entry_date <= endStr
    );
  } else if (viewMode === 'month') {
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    
    const startStr = startOfMonth.toISOString().split('T')[0];
    const endStr = endOfMonth.toISOString().split('T')[0];
    
    entries = timeEntries.filter(entry => 
      entry.entry_date >= startStr && entry.entry_date <= endStr
    );
  }
  
  // Now apply all the filters client-side
  
  // Status filter
  if (filters.status && filters.status !== 'all') {
    entries = entries.filter(entry => entry.status === filters.status);
  }
  
  // Draft age filter
  if (filters.draftAge === 'old' && filters.status === 'draft') {
    entries = entries.filter(entry => {
      if (entry.status !== 'draft') return false;
      const created = new Date(entry.created_at || entry.entry_date);
      const daysSince = (Date.now() - created) / (1000 * 60 * 60 * 24);
      return daysSince > 3;
    });
  }
  
  // Client filter
  if (filters.clientId && filters.clientId !== 'all') {
    entries = entries.filter(entry => entry.client_id === filters.clientId);
  }
  
  // Project filter
  if (filters.projectId && filters.projectId !== 'all') {
    entries = entries.filter(entry => entry.project_id === filters.projectId);
  }
  
  // Priority filter
  if (filters.priority && filters.priority !== 'all') {
    entries = entries.filter(entry => entry.priority === filters.priority);
  }
  
  // Billable filter
  if (filters.billable && filters.billable !== 'all') {
    if (filters.billable === 'billable') {
      entries = entries.filter(entry => entry.is_billable === true);
    } else {
      entries = entries.filter(entry => entry.is_billable === false);
    }
  }
  
  // Search filter
  if (filters.search && filters.search.trim() !== '') {
    const searchLower = filters.search.toLowerCase();
    entries = entries.filter(entry => 
      entry.description?.toLowerCase().includes(searchLower) ||
      entry.notes?.toLowerCase().includes(searchLower) ||
      entry.task_name?.toLowerCase().includes(searchLower)
    );
  }
  
  // Date range filters
  if (filters.startDate) {
    entries = entries.filter(entry => entry.entry_date >= filters.startDate);
  }
  if (filters.endDate) {
    entries = entries.filter(entry => entry.entry_date <= filters.endDate);
  }
  
  return entries;
}, [timeEntries, viewMode, currentDate, filters]);

 // Calculate stats (memoized for efficiency)
  const totalTime = useMemo(() => 
    timeEntries.reduce((total, entry) => total + (entry.duration_hours || 0), 0), [timeEntries]
  );
  const pendingEntries = useMemo(() => 
    timeEntries.filter(e => e.status === 'submitted' || e.status === 'in_review').length, [timeEntries]
  );
  const approvedEntries = useMemo(() => 
    timeEntries.filter(e => e.status === 'approved').length, [timeEntries]
  );
  const needsApproval = useMemo(() => 
    timeEntries.filter(e => 
      (e.status === 'submitted' || e.status === 'in_review') && 
      (userRole === 'admin' || userRole === 'manager')
    ).length, [timeEntries, userRole]
  );
  const oldDrafts = useMemo(() => 
    timeEntries.filter(e => {
      if (e.status !== 'draft') return false;
      const created = new Date(e.created_at || e.entry_date);
      const daysSince = (Date.now() - created) / (1000 * 60 * 60 * 24);
      return daysSince > 3;
    }).length, [timeEntries]
  );

  // NEW: Optimized stat click handler - batch state updates to minimize re-renders
  const handleStatClick = (filterType) => {
  // Single atomic state update
  setFilters(prev => {
    const newFilters = { ...prev };
    
    switch(filterType) {
      case 'needs_approval':
      case 'pending':
        newFilters.status = 'submitted';
        newFilters.draftAge = 'all'; // Reset draft age filter
        break;
      case 'old_drafts':
        newFilters.status = 'draft';
        newFilters.draftAge = 'old'; // NEW: Set to show only old drafts
        break;
      case 'drafts':
        newFilters.status = 'draft';
        newFilters.draftAge = 'all'; // Show all drafts
        break;
      case 'approved':
        newFilters.status = 'approved';
        newFilters.draftAge = 'all';
        break;
      case 'total':
        newFilters.status = 'all';
        newFilters.draftAge = 'all';
        break;
      default:
        break;
    }
    
    return newFilters;
  });
  
  // Switch to list view for better visibility
  setViewMode('list');
};

  if (entriesLoading || loading) {
    return (
      <div className="p-12 text-center">
        <RefreshCw className="w-8 h-8 text-slate-400 mx-auto mb-4 animate-spin" />
        <div className="text-lg text-slate-500">Loading entries...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Time Entries</h1>
          <p className="text-slate-600 mt-1">
            Review and manage time entries with enhanced workflow capabilities
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600'
              }`}
            >
              <List className="w-4 h-4" />
              <span className="text-sm font-medium">List</span>
            </button>
            <CalendarViewModeSelector 
              viewMode={viewMode === 'list' ? 'week' : viewMode}
              onViewModeChange={(mode) => setViewMode(mode)}
              className="border-none bg-transparent p-0"
            />
          </div>
        </div>
      </div>

      {/* Enhanced Quick Stats */}
      <EnhancedQuickStats 
        timeEntries={timeEntries}
        visibleEntries={visibleEntries}
        filters={filters}
        viewMode={viewMode}
        currentDate={currentDate}
        userRole={userRole}
        teamViewMode={teamViewMode}
        onStatClick={handleStatClick}
      />

      {/* Enhanced Filters */}
      <EnhancedFilters 
        filters={filters}
        onFiltersChange={setFilters}
        clients={clients}
        projects={projects}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />

      {/* Toolbar: My/Team + Group By inline */}
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <TeamViewToggle 
          viewMode={teamViewMode}
          onViewModeChange={handleTeamViewChange}
          userRole={userRole}
        />
        <GroupBySelector
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          userRole={userRole}
          compact
        />
      </div>

      {/* Selection Controls */}
      {visibleEntries.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Showing {visibleEntries.length} entries
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setSelectedEntries(
                selectedEntries.length === visibleEntries.length 
                  ? [] 
                  : visibleEntries.map(e => e.id)
              )}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {selectedEntries.length === visibleEntries.length ? 'Deselect All' : 'Select All'}
            </button>
            {selectedEntries.length > 0 && (
              <span className="text-sm text-slate-600">
                ({selectedEntries.length} selected)
              </span>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <MainContent
        viewMode={viewMode}
        timeEntries={timeEntries}
        groupBy={groupBy}
        clients={clients}
        projects={projects}
        teamMembers={teamMembers}
        selectedEntries={selectedEntries}
        onToggleSelection={toggleEntrySelection}
        onEdit={handleEditEntry}
        onDuplicate={handleDuplicateEntry}
        onDelete={handleDeleteEntry}
        onView={handleEntryView}
        onStatusUpdate={handleStatusUpdate}
        onSplit={handleSplitEntry}
        onRevertSplit={handleRevertSplit}
        onAddComment={handleAddComment}
        onUpdateTags={handleUpdateTags}
        userRole={userRole}
        currentDate={currentDate}
        onDateChange={setCurrentDate}
        onEntryCreate={handleEntryCreate}
        goals={goals}
      />
      
      {/* Split Time Modal */}
      <SplitTimeModal
        isOpen={showSplitModal}
        onClose={() => {
          setShowSplitModal(false);
          setSelectedEntryForSplit(null);
        }}
        entry={selectedEntryForSplit}
        onSplitComplete={handleSplitComplete}
        projects={projects}
        clients={clients}
      />
      
      {/* Bulk Actions */}
      <BulkActions 
        selectedEntries={selectedEntries}
        onBulkAction={handleBulkAction}
        userRole={userRole}
      />
    </div>
  );
};

export default EnhancedEntriesView;